import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

export type WorkflowStep = {
  title: string
  body: ReactNode
}

/** Numbered operator workflow — matches Lab Backend-UI step copy style. */
export function CatalogWorkflow({ steps }: { steps: WorkflowStep[] }) {
  return (
    <ol className="cm-steps">
      {steps.map((step) => (
        <li key={step.title}>
          <strong>{step.title}</strong>
          {' — '}
          {step.body}
        </li>
      ))}
    </ol>
  )
}

export type LegendItem = { label: string; description: string }

/** Collapsible control glossary (market selection toggles, etc.). */
export function CatalogLegend({ items }: { items: LegendItem[] }) {
  return (
    <details className="cm-legend">
      <summary>What do these controls mean?</summary>
      <dl className="cm-legend-list">
        {items.map((item) => (
          <div key={item.label} className="cm-legend-row">
            <dt>{item.label}</dt>
            <dd>{item.description}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

export function CatalogSection({
  title,
  description,
  children,
  card = true,
}: {
  title: string
  description?: ReactNode
  children: ReactNode
  card?: boolean
}) {
  return (
    <section className={card ? 'cm-catalog-card cm-section' : 'cm-section'}>
      <header className="cm-section-head">
        <h3 className="cm-section-title">{title}</h3>
        {description ? <p className="cm-section-desc">{description}</p> : null}
      </header>
      {children}
    </section>
  )
}

export const CATALOG_OVERVIEW: WorkflowStep[] = [
  {
    title: 'Pull data',
    body: (
      <>
        Use{' '}
        <Link to="/catalog/manual">Manual catalog</Link> or{' '}
        <Link to="/catalog/betfair-fetch">Betfair Market Fetch</Link> to import events and markets
        from the exchange.
      </>
    ),
  },
  {
    title: 'Choose leagues',
    body: (
      <>
        Turn on <Link to="/catalog/automation">Automation</Link> so cron workers fetch on schedule.
      </>
    ),
  },
  {
    title: 'Enable for players',
    body: (
      <>
        Use <Link to="/catalog/market-selection">Market selection</Link> to turn markets on/off,
        set max bet, scorecard (Betradar), and live TV — including fancy and bookmaker on the same
        event.
      </>
    ),
  },
  {
    title: 'Inspect',
    body: (
      <>
        <Link to="/catalog/browse">Browse catalog</Link> is read-only drill-down: league → event →
        market list. Edit markets from Market selection.
      </>
    ),
  },
]

export const MARKET_TOGGLE_LEGEND: LegendItem[] = [
  {
    label: 'Event status',
    description: 'Master on/off for the whole match. Off hides all markets under this event.',
  },
  {
    label: 'Market status',
    description: 'Show or hide this market on the player portal.',
  },
  {
    label: 'Re-Active',
    description:
      'Match Odds only. Manually re-open a market after auto-close (Lab “Re-Active”).',
  },
  {
    label: 'In-play',
    description: 'Open or close in-play betting for this market.',
  },
  {
    label: 'Suspend',
    description: 'Temporarily suspend the market (rates may still update).',
  },
  {
    label: 'Only back',
    description: 'Players can back only — no lay side.',
  },
  {
    label: 'Betradar',
    description: 'Enable Sportradar scorecard on Match Odds (needs Betradar id in Edit).',
  },
  {
    label: 'Video',
    description: 'Show live TV for this match when a video URL is configured.',
  },
  {
    label: 'Betfair odds',
    description: 'Use exchange odds on the portal (off = internal/mock rates only).',
  },
  {
    label: 'Edit',
    description: 'Max bet, max market, fancy limits, video URL, Betradar id, Stats id, in-play time.',
  },
]
